import { GenericObject, Nullable } from '@dxb-web-ui-framework/common/types';
import { isEmpty } from '@dxb-web-ui-framework/common/utils/Helper';
import React, { useCallback, useState, VFC } from 'react';
import { Box } from '@dxb-web-ui-framework/design-system';
import { ErrorAlert } from '../../../../content-composer/src/components/Generic/ErrorAlert/ErrorAlert';
import { TableGrid } from '../../../../content-composer/src/components/Generic/TableGrid';
import { MandatePayroll } from '../../domain/Authorization/entities';
import { PayrollDetailsDrawer } from '../../../../payroll/src/components';
import {
  useAuthorizationPayrollDetails,
  useAuthorizationPayrollErrorState,
  useAuthorizationPayrollLoadingState,
  useIsPayrollAuthFlowDrawerOpen,
  useOnResetSelectedAuthorizationPayroll,
  useOnSelectAuthorizationPayroll,
  useOnTogglePayrollAuthFlowDrawer,
} from '../../contexts/Payroll/hooks';
import { ProcessStatuses } from '../ApproveRejectAuthFlowPresenter';
import { ApproveRejectPayrollDrawer } from './ApproveRejectPayrollDrawer';
import { AuthorizationPayrollDetailsContent } from './AuthorizationPayrollDetailsContent';
import { useAuthorizationPayrollGridConfig } from './useAuthorizationPayrollGridConfig';

export interface AuthorizationsPayrollTableProps {
  payrolls: Nullable<MandatePayroll[]>;
  isLoading: boolean;
  payrollListError?: any;
  totalPages: number;
  currentPage: number;
  onPageChange: (event: React.ChangeEvent<unknown>, page: number) => void;
  classes: GenericObject;
}

const AuthorizationsPayrollTable: VFC<AuthorizationsPayrollTableProps> = ({
  payrolls,
  isLoading,
  payrollListError,
  classes,
}) => {
  const isAuthFlowDrawerOpen = useIsPayrollAuthFlowDrawerOpen();
  const onToggleAuthFlowDrawer = useOnTogglePayrollAuthFlowDrawer();
  const [authAction, setAuthAction] = useState<null | string>(null);
  const onSelectPayroll = useOnSelectAuthorizationPayroll();
  const [isPayrollDetailsDrawerOpen, setIsPayrollDetailsDrawerOpen] = useState(false);
  const payrollDetails = useAuthorizationPayrollDetails();
  const isLoadingPayrollDetails = useAuthorizationPayrollLoadingState('isLoadingPayrollDetails');
  const hasErrorPayrollDetails = useAuthorizationPayrollErrorState('payrollDetailsError');
  const resetSelectedPayroll = useOnResetSelectedAuthorizationPayroll();

  const onTogglePayrollDetailsDrawer = useCallback(
    (payrollFileId: string) => {
      setIsPayrollDetailsDrawerOpen(prev => !prev);
      if (!isPayrollDetailsDrawerOpen) {
        onSelectPayroll(payrollFileId);
      } else {
        resetSelectedPayroll();
      }
    },
    [isPayrollDetailsDrawerOpen, onSelectPayroll, resetSelectedPayroll],
  );

  const onViewDetails = useCallback(
    (payrollFileId: string) => {
      onTogglePayrollDetailsDrawer(payrollFileId);
      onSelectPayroll(payrollFileId);
      setAuthAction(null);
    },
    [onTogglePayrollDetailsDrawer, onSelectPayroll],
  );

  const onSelectListItem = useCallback(
    (rowData: MandatePayroll) => {
      if (rowData.processStatus === ProcessStatuses.PENDING) {
        return;
      }
      onSelectPayroll(rowData.payrollFileId);
      setIsPayrollDetailsDrawerOpen(true);
    },
    [onSelectPayroll],
  );

  const actionCallback = useCallback(
    (event: any, name: string, payrollFileId: string) => {
      switch (name) {
        case 'approve':
          onToggleAuthFlowDrawer();
          onSelectPayroll(payrollFileId);
          setAuthAction(name);
          break;
        case 'reject':
          onToggleAuthFlowDrawer();
          onSelectPayroll(payrollFileId);
          setAuthAction(name);
          break;
        default:
          onViewDetails(payrollFileId!);
      }
    },
    [onToggleAuthFlowDrawer, onViewDetails, onSelectPayroll],
  );

  const config = useAuthorizationPayrollGridConfig(actionCallback, onTogglePayrollDetailsDrawer, onSelectListItem);

  const shouldShowTable = isEmpty(payrollListError);
  return (
    <>
      {!shouldShowTable && (
        <Box mx={3} mt={2}>
          <ErrorAlert message={payrollListError.message} />
        </Box>
      )}
      {shouldShowTable && (
        <TableGrid
          gridOptions={config.gridOptions}
          columnDefs={config.columnDefs}
          rowData={payrolls}
          hasSkeleton={isLoading}
          mobileListConfig={config.mobileListConfig}
          isRowSelected={config.isRowSelected}
        />
      )}
      <PayrollDetailsDrawer isOpen={isPayrollDetailsDrawerOpen} onToggleDrawer={onViewDetails}>
        <AuthorizationPayrollDetailsContent
          payrollDetails={payrollDetails}
          isLoading={isLoadingPayrollDetails}
          error={hasErrorPayrollDetails}
          isAuthPayroll
          setAuthAction={setAuthAction}
          onToggle={onToggleAuthFlowDrawer}
          authAction={authAction}>
          <ApproveRejectPayrollDrawer
            isOpen={isAuthFlowDrawerOpen}
            onToggle={onToggleAuthFlowDrawer}
            authAction={authAction}
          />
        </AuthorizationPayrollDetailsContent>
      </PayrollDetailsDrawer>
      <ApproveRejectPayrollDrawer
        isOpen={isAuthFlowDrawerOpen}
        onToggle={() => {
          onToggleAuthFlowDrawer();
          if (isPayrollDetailsDrawerOpen) {
            onViewDetails('');
          }
        }}
        authAction={authAction}
      />
    </>
  );
};

export { AuthorizationsPayrollTable };
